import React, { useState } from 'react';
import ASTGridEditor from './ASTGridEditor';
import { IDENTIFICATION_METHODS } from '../utils/microbiologyTemplateEngine';

export default function OrganismASTSection({
  data,
  organisms = [],
  antimicrobials = [],
  activeIsolateIdx,
  onSelectIsolate,
  onAddIsolate,
  onDeleteIsolate,
  onChangeOrganism,
  onUpdateIsolateDetails,
  onASTValueChange,
  onAddDrugToIsolate,
  onOpenOverride,
  onToggleReportable,
  onBulkUpdateAST
}) {
  const [organismGroupFilter, setOrganismGroupFilter] = useState('ALL');
  const [organismSearch, setOrganismSearch] = useState('');

  const currentIsolate = data?.isolates?.[activeIsolateIdx] || null;
  const isolateList = data?.isolates || [];

  // Filter organisms by group and search
  const filteredOrganisms = organisms.filter((o) => {
    const matchesSearch =
      !organismSearch ||
      (o.organismName && o.organismName.toLowerCase().includes(organismSearch.toLowerCase())) ||
      (o.shortName && o.shortName.toLowerCase().includes(organismSearch.toLowerCase()));

    const matchesGroup =
      organismGroupFilter === 'ALL' ||
      (organismGroupFilter === 'GNB' && (o.gramType?.toLowerCase().includes('negative') || o.gramType?.toLowerCase().includes('bacilli'))) ||
      (organismGroupFilter === 'GPC' && (o.gramType?.toLowerCase().includes('positive') || o.gramType?.toLowerCase().includes('cocci'))) ||
      (organismGroupFilter === 'YEAST' && (o.gramType?.toLowerCase().includes('yeast') || o.gramType?.toLowerCase().includes('fung')));

    return matchesSearch && matchesGroup;
  });

  return (
    <div className="wb-stage-view">
      {/* 1. Multi-Isolate Tabs Bar */}
      <div className="wb-isolate-tabs-strip">
        <div className="wb-isolate-tabs-list">
          {isolateList.map((iso, idx) => {
            const sCount = (iso.astResults || []).filter((r) => r.finalInterpretation === 'S').length;
            const rCount = (iso.astResults || []).filter((r) => r.finalInterpretation === 'R').length;

            return (
              <button
                key={iso.isolateID || idx}
                type="button"
                className={`wb-isolate-tab ${activeIsolateIdx === idx ? 'active' : ''}`}
                onClick={() => onSelectIsolate(idx)}
              >
                <span className="wb-isolate-badge">#{iso.isolateNumber || idx + 1}</span>
                <strong className="wb-isolate-label">
                  {iso.shortName || iso.organismName || 'Unidentified Isolate'}
                </strong>
                {iso.gramType && (
                  <span className="wb-tag-gram">{iso.gramType}</span>
                )}
                {iso.astResults?.length > 0 && (
                  <span className="wb-tag-ast-stat">
                    {sCount}S / {rCount}R
                  </span>
                )}
              </button>
            );
          })}

          <button
            type="button"
            className="wb-btn-add-isolate"
            onClick={onAddIsolate}
            title="Identify additional bacterial/fungal isolate in this culture"
          >
            + Add Isolate
          </button>
        </div>
      </div>

      {currentIsolate ? (
        <div className="wb-isolate-body">
          {/* 2. Isolate Identification & Metadata Strip */}
          <div className="wb-isolate-config-card">
            <div className="wb-isolate-config-header">
              <span className="wb-isolate-title">
                Isolate #{currentIsolate.isolateNumber || activeIsolateIdx + 1}: Identification & Growth Profile
              </span>

              {isolateList.length > 1 && (
                <button
                  type="button"
                  className="wb-btn-text-danger"
                  onClick={() => onDeleteIsolate(currentIsolate.isolateID)}
                  title="Remove this isolate from culture"
                >
                  Delete Isolate
                </button>
              )}
            </div>

            <div className="wb-clinical-form-layout">
              {/* Left Column: Organism & Identification Method */}
              <div className="wb-form-column">
                <div className="wb-field-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label className="wb-field-label" style={{ margin: 0 }}>
                      Identified Microorganism <span className="wb-req">*</span>
                    </label>
                    {/* Quick Filters */}
                    <div className="wb-filter-pills">
                      <input
                        type="text"
                        placeholder="Search organism..."
                        value={organismSearch}
                        onChange={(e) => setOrganismSearch(e.target.value)}
                        className="wb-input-search-inline"
                      />
                      {['ALL', 'GNB', 'GPC', 'YEAST'].map((grp) => (
                        <button
                          key={grp}
                          type="button"
                          className={`wb-filter-pill ${organismGroupFilter === grp ? 'active' : ''}`}
                          onClick={() => setOrganismGroupFilter(grp)}
                        >
                          {grp === 'ALL' ? 'All' : grp === 'GNB' ? 'Gram -ve' : grp === 'GPC' ? 'Gram +ve' : 'Yeast'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <select
                    className="wb-select"
                    value={currentIsolate.organismID}
                    onChange={(e) => onChangeOrganism(e.target.value)}
                  >
                    {filteredOrganisms.map((o) => (
                      <option key={o.organismID} value={o.organismID}>
                        {o.organismName} ({o.gramType || 'Bacteria'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wb-field-group">
                  <label className="wb-field-label">Identification Method</label>
                  <select
                    className="wb-select"
                    value={currentIsolate.identificationMethod || IDENTIFICATION_METHODS[0]}
                    onChange={(e) =>
                      onUpdateIsolateDetails({
                        ...currentIsolate,
                        identificationMethod: e.target.value
                      })
                    }
                  >
                    {IDENTIFICATION_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right Column: Morphology & Colony Density */}
              <div className="wb-form-column">
                <div className="wb-field-group">
                  <label className="wb-field-label">Relative Colony Density</label>
                  <select
                    className="wb-select"
                    value={currentIsolate.quantityDescription || 'Heavy Growth (> 10^5 CFU/mL)'}
                    onChange={(e) =>
                      onUpdateIsolateDetails({
                        ...currentIsolate,
                        quantityDescription: e.target.value
                      })
                    }
                  >
                    <option value="Heavy Growth (> 10^5 CFU/mL)">Heavy Growth (&gt; 10^5 CFU/mL)</option>
                    <option value="Moderate Growth (10^4 - 10^5 CFU/mL)">Moderate Growth (10^4 - 10^5 CFU/mL)</option>
                    <option value="Scanty / Few Colonies (< 10^4 CFU/mL)">Scanty / Few Colonies (&lt; 10^4 CFU/mL)</option>
                    <option value="Pure Monomorphic Growth">Pure Monomorphic Growth</option>
                  </select>
                </div>

                <div className="wb-field-group">
                  <label className="wb-field-label">Colony Morphology</label>
                  <input
                    type="text"
                    className="wb-input"
                    value={currentIsolate.colonyMorphology || ''}
                    onChange={(e) =>
                      onUpdateIsolateDetails({
                        ...currentIsolate,
                        colonyMorphology: e.target.value
                      })
                    }
                    placeholder="Colony characteristics (e.g. Smooth pink lactose-fermenting)..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 3. Embedded Keyboard-Friendly AST Susceptibility Grid */}
          <ASTGridEditor
            isolate={currentIsolate}
            antimicrobials={antimicrobials}
            onValueChange={onASTValueChange}
            onAddDrug={onAddDrugToIsolate}
            onOpenOverride={onOpenOverride}
            onToggleReportable={onToggleReportable}
            onBulkUpdate={onBulkUpdateAST}
          />
        </div>
      ) : (
        <div className="wb-empty-state-clean">
          <p>No organism isolates added to this culture yet.</p>
          <button
            type="button"
            className="wb-btn-primary"
            onClick={onAddIsolate}
          >
            + Identify First Isolate
          </button>
        </div>
      )}
    </div>
  );
}
